/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import { Fragment, ReactElement, useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer'
import ProductPrice from '../../../lib/ProductPrice'
import { FeaturedArticleShowContext } from '../../FeaturedArticles/Show'
import { ProductForProductsComponentProp } from '../../types'

type Props = {
  product: ProductForProductsComponentProp
  order: number
  viewMore: boolean
}

function ProductComponent({ product, order, viewMore }: Props): ReactElement {
  const productStyle = () =>
    css`
      box-sizing: border-box;
      width: 50%;
      padding: ${order % 2 === 0 ? '0 6px 12px 0' : '0 0 12px 6px'};
      display: ${order > 3 && !viewMore ? 'none' : 'block'};
    `
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment
  const { ref, inView } = useInView({ triggerOnce: true })
  const featuredArticleShowContext = useContext(FeaturedArticleShowContext)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    inView &&
      featuredArticleShowContext &&
      featuredArticleShowContext.logFeaturedArticleEvent({
        event: 'product_component_imp',
        contentType: product.className,
        contentId: product.id,
        shopId: product.shop.id,
      })
  }, [featuredArticleShowContext, inView, product])

  const productClick = () => {
    featuredArticleShowContext.logFeaturedArticleEvent({
      event: 'product_component_click',
      contentType: product.className,
      contentId: product.id,
      shopId: product.shop.id,
    })
  }

  return (
    <Fragment>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <li ref={ref} css={productStyle()}>
        <a href={product.path} css={productLinkStyle} onClick={productClick}>
          <div css={productImageWrapperStyle}>
            <img src={product.image.url} width="170" height="90" css={productImageStyle} />
          </div>
          <div css={productTextWrapperStyle}>
            {product.comment && <div css={productCommentStyle}>{product.comment}</div>}
            <div css={productNameStyle}>{product.name}</div>
            <div css={shopNameStyle}>{product.shop.name}</div>
            <ProductPrice product={product} wrapperStyle={productPriceStyle} />
          </div>
        </a>
      </li>
    </Fragment>
  )
}

const productLinkStyle = css`
  display: block;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  border-radius: 4px;
  background-color: #ffffff;
  overflow: hidden;
`

const productImageWrapperStyle = css`
  border-radius: 4px;
  position: relative;
`

const productImageStyle = css`
  display: block;
  width: 100%;
  height: auto;
`

const productTextWrapperStyle = css`
  padding: 8px;
`

const productCommentStyle = css`
  margin-bottom: 4px;
  font-weight: bold;
  font-size: 12px;
  line-height: 130%;
  color: #835f4d;
`

const productNameStyle = css`
  margin-bottom: 4px;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 120%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`

const productPriceStyle = css`
  margin-top: 8px;
  text-align: right;
`

const shopNameStyle = css`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 130%;
  color: rgba(0, 0, 0, 0.54);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`

export default ProductComponent
